export const ROUTES = {
    HOME: '/',
    HELP: '/help',

    AUTH: {
        ROOT: '/auth',
        LOGIN: '/auth/login',
        SELECT_CLIENT: '/auth/select-client',
        SUCCESS: '/auth/success',
    },

    SELF_SERVICE: {
        ROOT: '/self-service',
        POLICIES: '/self-service/policies',
        CLAIMS: '/self-service/claims',
        PAYMENTS: '/self-service/payments',
        REQUESTS: '/self-service/requests',
        MESSAGES: '/self-service/messages',

        PROFILE: {
            ROOT: '/self-service/profile',
            UPDATE: '/self-service/profile/update',
            REGISTER: '/self-service/profile/register',
        },

        LIFE_INSURANCE: {
            ROOT: '/self-service/life-insurance',
            DETAIL: (id: string) => `/self-service/life-insurance/${id}`,
            CLAIMS: '/self-service/life-insurance/claims',
            INVOICES: '/self-service/life-insurance/invoices',
            GROUP_INVOICES: '/self-service/life-insurance/group-invoices',
            PAYMENT_LOYALTY: (id: string) => `/self-service/life-insurance/${id}/payment-loyalty`,
            PAYMENT_HISTORY: (id: string) => `/self-service/life-insurance/${id}/payment-history`,
            INVOICES_HISTORY: (id: string) => `/self-service/life-insurance/${id}/invoices`,
            PAYMENT_FEES: (id: string) => `/self-service/life-insurance/${id}/payment-fees`,
            TERMS_AND_CONDITIONS: (id: string) => `/self-service/life-insurance/${id}/terms-and-conditions`,
            PERSON_COVERS: (pageId: string, personId: string) =>
                `/self-service/life-insurance/${pageId}/covers/${personId}`,
        },

        E_HEALTH_INSURANCE: {
            ROOT: '/self-service/e-health-insurance',
            CLAIMS: '/self-service/e-health-insurance/claims',
            CARDS_AND_LIMITS: '/self-service/e-health-insurance/cards-and-limits',
        },

        SEESAM_HEALTH: {
            ROOT: '/self-service/seesam-health',
            CARDS_AND_LIMITS: '/self-service/seesam-health/cards-and-limits',
        },

        PROPERTY_INSURANCE: '/self-service/property-insurance',
        TRAVEL_INSURANCE: '/self-service/travel-insurance',

        NON_LIFE_INSURANCE: {
            CLAIMS: '/self-service/non-life-insurance/claims',
        },

        CNL_PAYMENTS: {
            PENDING_PAYMENTS: '/self-service/cnl-payments/pending-payments',
            SUBSCRIPTIONS: '/self-service/cnl-payments/subscriptions',
        },

        PUBLIC_INVESTMENTS: '/self-service/public-investments',

        WIZARDS: {
            CHANGE_AGREEMENT: '/self-service/change-agreement',
            CHANGE_BENEFICIARY_DEATH: (id: string) => `/self-service/change-beneficiary-death/${id}`,
            CHANGE_BENEFICIARY_SURVIVAL: (id: string) => `/self-service/change-beneficiary-survival/${id}`,
            CHANGE_COVERS: (id: string) => `/self-service/change-covers/${id}`,
            CHANGE_INSURED_PERSON: (id: string) => `/self-service/change-insured-person/${id}`,
            CHANGE_INVESTMENT_FUNDS: (id: string) => `/self-service/change-investment-funds/${id}`,
            CHANGE_PAYMENTS: (id: string) => `/self-service/change-payments/${id}`,
            CHANGE_RISK_REVIEW: (id: string) => `/self-service/change-risk-review/${id}`,
            CHANGE_VALIDITY_PERIOD: (id: string) => `/self-service/change-validity-period/${id}`,
            HEALTH_CLAIM: '/self-service/health-claim',
            LIFE_CLAIM: '/self-service/life-claim',
            EMAIL_COLLECTION: (eventId: string, insuredPersonId: string, fillerId: string) =>
                `/self-service/forms/email-collection?eventId=${eventId}&ipId=${insuredPersonId}&fillerId=${fillerId}`,
            TRANSPORT_CLAIM: '/self-service/transport-claim',
            PROPERTY_CLAIM: '/self-service/property-claim',
            PERSONAL_ACCIDENT_CLAIM: '/self-service/personal-accident-claim',
            TRAVEL_CLAIM: '/self-service/travel-claim',
            GENERAL_CLAIM: '/self-service/general-claim',
        },
    },

    ESALES: {
        TRANSPORT: {
            ROOT: '/esales/transport',
            SUCCESS: '/esales/transport/success',
            CALCULATOR_LT: '/transporto-draudimas-skaiciuokle',
        },
        LIFE: {
            ROOT: '/esales/life',
            SUCCESS: '/esales/life/success',
        },
        TRAVEL: {
            ROOT: '/esales/travel',
            SUCCESS: '/esales/travel/success',
            ALTERNATE: '/keliones-draudimas-skaiciuokle',
        },
        PERSONAL_ACCIDENTS: {
            ROOT: '/esales/personal-accidents',
            SUCCESS: '/esales/personal-accidents/success',
            ALTERNATE: '/nelaimingu-atsitikimu-draudimas-skaiciuokle',
        },
        HOME: {
            ROOT: '/esales/home',
            SUCCESS: '/esales/home/success',
            ALTERNATE: '/turto-draudimas-skaiciuokle',
        },
        FAILURE: '/esales/failure',
    },

    API: {
        AUTH: {
            SWEDBANK: '/api/auth/swedbank',
            SEB: '/api/auth/seb',
        },
    },
};

export const RouteMatchers = {
    isEsalesTransport: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.TRANSPORT.ROOT) || path.includes(ROUTES.ESALES.TRANSPORT.CALCULATOR_LT);
    },

    isEsalesTravel: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.TRAVEL.ROOT) || path.includes(ROUTES.ESALES.TRAVEL.ALTERNATE);
    },

    isEsalesHome: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.HOME.ROOT) || path.includes(ROUTES.ESALES.HOME.ALTERNATE);
    },

    isEsalesLife: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.ESALES.LIFE.ROOT);
    },

    isEsalesPersonalAccidents: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return (
            path.includes(ROUTES.ESALES.PERSONAL_ACCIDENTS.ROOT) ||
            path.includes(ROUTES.ESALES.PERSONAL_ACCIDENTS.ALTERNATE)
        );
    },

    isEsales: (path: Nullable<string>): boolean => {
        return (
            RouteMatchers.isEsalesTransport(path) ||
            RouteMatchers.isEsalesTravel(path) ||
            RouteMatchers.isEsalesLife(path) ||
            RouteMatchers.isEsalesPersonalAccidents(path) ||
            RouteMatchers.isEsalesHome(path)
        );
    },

    isSelfService: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.SELF_SERVICE.ROOT);
    },

    isAuth: (path: Nullable<string>): boolean => {
        if (!path) return false;
        return path.includes(ROUTES.AUTH.ROOT);
    },
};
